import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { CalibrationTrainer } from '../../calibration/CalibrationTrainer';
import {
  createCalibrationFlow,
  createCooperativeHeroCalibration,
} from '../../../interaction/calibration';
import type { CalibrationProfile } from '../../../interaction/calibration';
import type { CameraSession } from '../../../interaction/camera';
import type { LaneResolver } from '../../../interaction/pose';
import type { CameraMode } from '../../../shared/types/camera-mode';
import { INVENTED_CAMERA_OVERRIDE_COPY } from '../../../platform/config/camera-mode';
import { stampCameraDeviceId } from '../../../interaction/calibration';
import type { ThemeAssetResolver } from '../../../sdk';
import type { AssetId } from '../../../shared/types/ids';
import { StageArt } from './StageArt';

/**
 * Hero training, run over a mission that is paused and stays paused (GDS §32, D-059 PS3-2).
 *
 * The whole of PS3-2's ruling is structural here: **nothing in this file touches the mission**. It
 * has no runtime, no round, no score and no pause of its own — a grown-up paused the mission before
 * they could reach the control that mounts this, the mission is held by that ordinary pause the
 * entire time, and it is their Resume that ends the episode. There is no new runtime state because
 * there is nothing here that could have introduced one.
 *
 * It renders no camera picture of its own either. The sitting's mirrored feed is already on screen
 * behind the stage, and a second `<video>` would mean a second element competing for the one stream
 * the camera session owns (architecture §23: exactly one owner, or "stop the tracks on exit" has
 * two owners and therefore none).
 *
 * The measured profile is saved to the sitting's own session-only store the moment training
 * completes, and **applying** it is deliberately somebody else's job (the stage, on Resume): a child
 * mid-round must never have the numbers change under them.
 */

interface StageCalibrationOverlayBaseProps {
  camera: CameraSession;
  /**
   * The running game's pictures for this screen, resolved by role (Slice E″).
   *
   * Passed in rather than looked up: the training screen is the platform's and must keep knowing
   * nothing about which module is loaded. Optional, so a caller with no theme to hand — and a theme
   * that declares neither role — simply gets the wordy panel it has always had.
   */
  art?: {
    resolver: ThemeAssetResolver;
    roles: { calibrationBodyGuide: AssetId; calibrationSuccess: AssetId };
  } | null;
  /** Whose body — the training key, not the lane (D-064's "keyed by the child"). */
  playerId: string;
  cameraMode: CameraMode;
  /** Called once training has produced a profile. The stage decides what to do with it. */
  onCaptured: (profile: CalibrationProfile) => void;
  /** The grown-up backed out. Nothing is measured, nothing is replaced. */
  onAbandon: () => void;
  /** What the stage wants said under the trainer once a profile is waiting for Resume. */
  footnote?: string | null;
}

/**
 * A cooperative retraining cannot be mounted without the sitting's own lane view (S3).
 *
 * A union rather than an optional prop, so that "cooperative" and "reads the one identity owner"
 * are the same fact and a caller cannot supply half of it. The factory shape is about lifetime: the
 * view suspends this hero's identity calibration while the training runs and puts it back when the
 * flow is cancelled, so it is created with the flow rather than before it.
 */
export type StageCalibrationOverlayProps = StageCalibrationOverlayBaseProps &
  (
    | {
        /**
         * This is one hero of two, retraining while their sibling is in the room (Slice F′,
         * D-076(8)). The trainer is the **cooperative** one: portals are thirds of this hero's own
         * half of the picture (D-080(2)), and every reading the sitting's resolver will not
         * attribute to them becomes an empty frame — so the sibling standing at their own lane edge
         * is seen and never sampled (PS4-3).
         */
        cooperative: true;
        laneResolver: () => LaneResolver;
      }
    | { cooperative?: false; laneResolver?: undefined }
  );

export function StageCalibrationOverlay(props: StageCalibrationOverlayProps) {
  const {
    camera,
    playerId,
    cameraMode,
    onCaptured,
    onAbandon,
    footnote = null,
    art = null,
  } = props;
  const cooperative = props.cooperative === true;
  const laneResolver = props.cooperative === true ? props.laneResolver : null;
  const flow = useMemo(
    () =>
      laneResolver !== null
        ? createCooperativeHeroCalibration({
            playerId,
            viewport: { width: window.innerWidth, height: window.innerHeight },
            frames: camera.poseFrames,
            resolver: laneResolver(),
          })
        : createCalibrationFlow({
            playerId,
            viewport: { width: window.innerWidth, height: window.innerHeight },
            frames: camera.poseFrames,
            cameraMode,
          }),
    // `laneResolver` is deliberately not a dependency: it is the stage's stable borrow of the one
    // resolver, re-created on every render, and reacting to that would restart a child's training.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [camera, playerId, cameraMode, cooperative],
  );

  const state = useSyncExternalStore(
    flow.subscribe,
    () => flow.state,
    () => flow.state,
  );

  useEffect(() => {
    flow.attach();
    flow.start();

    // A training that is unmounted half-way is abandoned, not half-saved: half a set of
    // measurements is not a calibration (the CalibrationPage rule, unchanged here).
    return () => {
      flow.detach();
      flow.cancel();
    };
  }, [flow]);

  useEffect(() => {
    if (state.status === 'complete' && state.profile !== null) {
      // Session-only, on the sitting that owns it (GDS §13). No `thrive.v1.*` key anywhere.
      const measured = stampCameraDeviceId(state.profile, camera);
      camera.calibration.save(measured);
      onCaptured(measured);
    }
    // `onCaptured` is deliberately not a dependency: the stage re-creates its handler on every
    // render, and reacting to that would re-announce a profile that has already been handed over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera, state.status, state.profile]);

  const done = state.status === 'complete';

  return (
    <div className="panel" data-testid="stage-calibration">
      <h2 style={{ marginTop: 0 }}>{INVENTED_CAMERA_OVERRIDE_COPY.restartCalibrationRunning}</h2>

      {/*
       * The body outline while training runs, and the "that's it" picture once it is done.
       *
       * Decoration over the words, never instead of them: every instruction on this screen is still
       * a sentence, and the picture is hidden from assistive technology for exactly that reason.
       */}
      <StageArt
        assets={art?.resolver ?? null}
        assetId={
          art === null ? null : done ? art.roles.calibrationSuccess : art.roles.calibrationBodyGuide
        }
      />

      {done ? (
        <p className="lede" data-testid="stage-calibration-ready">
          {footnote ?? INVENTED_CAMERA_OVERRIDE_COPY.restartCalibrationReady}
        </p>
      ) : (
        <CalibrationTrainer flow={flow} state={state}>
          <button
            type="button"
            className="button button--quiet"
            data-testid="stage-calibration-abandon"
            onClick={(event) => {
              onAbandon();
              event.currentTarget.blur();
            }}
          >
            {INVENTED_CAMERA_OVERRIDE_COPY.restartCalibrationAbandon}
          </button>
        </CalibrationTrainer>
      )}
    </div>
  );
}
